import { useCallback, useEffect, useState } from "react";
import type { EstadoDaIa } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";

/**
 * A chave do modelo de linguagem — RN-61.
 *
 * **A chave entra e não volta.** O campo nasce vazio mesmo com uma cadastrada:
 * o que a tela mostra é "está cadastrada, e termina em 7f2a". Devolver a chave
 * para preencher o campo a colocaria em cache do navegador, no HTML da página e
 * em qualquer relatório de erro que serialize resposta — e ela é a conta de
 * cobrança de quem a cadastrou, não um dado de perfil.
 *
 * **Sem chave nenhuma o recurso não some da tela.** Os botões de IA continuam
 * onde estão, desabilitados, apontando para cá: um recurso que desaparece
 * quando não está configurado é indistinguível de um recurso que não existe.
 */
export function AjustesDaIa() {
  const [mostrar, setMostrar] = useState(false);
  const [estado, setEstado] = useState<EstadoDaIa | null>(null);
  const [chave, setChave] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setEstado(await pedir("ia.estado"));
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível ler o estado da IA.");
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function salvar() {
    setOcupado(true);
    setErro(null);
    setAviso(null);
    try {
      setEstado(await pedir("ia.salvarChave", { corpo: { chave } }));
      // O campo é limpo na hora: a chave não fica na tela depois de guardada,
      // nem sobra numa aba aberta esquecida.
      setChave("");
      setAviso("Chave guardada. Ela não volta para a tela.");
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível guardar a chave.");
    } finally {
      setOcupado(false);
    }
  }

  async function apagar() {
    setOcupado(true);
    setErro(null);
    try {
      setEstado(await pedir("ia.apagarChave"));
      setAviso("Chave apagada. As chamadas passam a usar a do servidor, se houver.");
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível apagar a chave.");
    } finally {
      setOcupado(false);
    }
  }

  if (!estado) {
    return (
      <section className="cartao cartao--recuado pilha" aria-busy="true">
        <span className="so-leitor" role="status">
          Carregando os ajustes de IA
        </span>
        <div className="esqueleto esqueleto--bloco" />
      </section>
    );
  }

  const restantes = Math.max(0, estado.cotaDiaria - estado.usadasHoje);

  return (
    <div className="ia-grade">
    <section className="ajustes__grupo ia-ajustes" aria-labelledby="ia-titulo">
      <h2 className="t-secao" id="ia-titulo">
        Modelo
      </h2>
      <p className="t-corpo">
        Usada para gerar questões, tirar dúvida sobre o item aberto e explicar um erro. As chamadas saem do servidor,
        nunca do navegador, e nada que ela responde é gravado sem você mandar.
      </p>

      <p className="t-ajuda">
        Em uso, nesta ordem: <strong>{estado.modelo}</strong>. Se um não responder, o próximo assume.
      </p>

      <div className="campo">
        <label className="campo__rotulo" htmlFor="ia-chave">
          Chave do Gemini (opcional)
        </label>
        <span className="campo-com-sufixo ia-ajustes__chave">
        <input
          id="ia-chave"
          className="campo__entrada"
          type={mostrar ? "text" : "password"}
          autoComplete="off"
          value={chave}
          onChange={(e) => setChave(e.target.value)}
          placeholder={estado.temChaveDaConta ? "cadastrada, digite outra para trocar" : "cole aqui a sua chave"}
        />
        <button type="button" className="link-mao link-mao--tinta" aria-pressed={mostrar} onClick={() => setMostrar((v) => !v)}>
          {mostrar ? "esconder" : "mostrar"}
        </button>
        </span>
        <p className="campo__ajuda">
          {estado.temChaveDaConta ? (
            <>
              Cadastrada, terminando em <span className="dado">{estado.finalDaChave}</span>. Ela é guardada cifrada e
              nunca volta para a tela.
            </>
          ) : estado.temChaveDoServidor ? (
            "Sem chave sua: as chamadas usam as do servidor. A sua, se cadastrar, entra no lugar da chave do Gemini do servidor."
          ) : (
            "Nenhuma chave configurada — nem sua, nem do servidor. Os recursos de IA ficam indisponíveis até haver uma."
          )}
        </p>
      </div>

      <div className="linha linha--quebra">
        <button type="button" className="botao" disabled={ocupado || chave.trim().length < 8} onClick={() => void salvar()}>
          {estado.temChaveDaConta ? "Trocar a chave" : "Guardar a chave"}
        </button>
        {estado.temChaveDaConta && (
          <button type="button" className="botao botao--secundario" disabled={ocupado} onClick={() => void apagar()}>
            Apagar a minha chave
          </button>
        )}
      </div>

      {erro && (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      )}
      <p className="campo__ajuda" aria-live="polite">
        {aviso ?? ""}
      </p>
    </section>

    <aside className="postit ia-grade__postit">
      <p className="postit__titulo">O que vai para o modelo</p>
      <p className="postit__texto">
        O assunto, o enunciado, as alternativas, o gabarito, a sua resposta e a explicação do item. Suas páginas do
        caderno não vão.
      </p>
      <p className="duvida-postit__sua">
        hoje: {estado.usadasHoje} de {estado.cotaDiaria} chamadas
        {restantes === 0 ? ", a cota volta na virada do dia" : ""}
      </p>
    </aside>
    </div>
  );
}
